/**
 * Phase 20 / Task 20.7 — the few styled primitives the import screens share.
 *
 * Sizes are the field-ergonomics contract (`docs/CLAUDE.md` §4, AC-6): every
 * button and nav target is at least 56px tall, every input and select at least
 * 52px, body text 16px. Colours come only from the theme tokens in styles.css,
 * so the screens are dark from day one with no per-component overrides.
 */

import * as React from 'react';

export const TOUCH_MIN = 56;
export const INPUT_MIN = 52;

/** Tinted backgrounds for §80's green / amber / red, built on the theme tokens. */
export const TONE = {
  ok: {
    bg: 'color-mix(in srgb, var(--color-success) 14%, transparent)',
    border: 'var(--color-success)',
  },
  warn: {
    bg: 'color-mix(in srgb, var(--color-warning) 16%, transparent)',
    border: 'var(--color-warning)',
  },
  error: {
    bg: 'color-mix(in srgb, var(--color-danger) 16%, transparent)',
    border: 'var(--color-danger)',
  },
} as const;

type Variant = 'primary' | 'secondary' | 'ghost';

export function ActionButton(
  props: React.ButtonHTMLAttributes<HTMLButtonElement> & { readonly variant?: Variant },
): React.JSX.Element {
  const { variant = 'secondary', style, disabled, ...rest } = props;
  const palette: Record<Variant, React.CSSProperties> = {
    primary: {
      backgroundColor: 'var(--color-accent-blue)',
      color: 'var(--color-on-accent)',
      border: '1px solid var(--color-accent-blue)',
    },
    secondary: {
      backgroundColor: 'var(--color-bg-tertiary)',
      color: 'var(--color-text-primary)',
      border: '1px solid var(--color-border-primary)',
    },
    ghost: {
      backgroundColor: 'transparent',
      color: 'var(--color-text-primary)',
      border: '1px solid transparent',
    },
  };
  return (
    <button
      type="button"
      disabled={disabled}
      style={{
        minHeight: `${TOUCH_MIN.toString()}px`,
        padding: '0 20px',
        borderRadius: '8px',
        fontSize: '16px',
        fontWeight: 600,
        cursor: disabled === true ? 'not-allowed' : 'pointer',
        opacity: disabled === true ? 0.5 : 1,
        ...palette[variant],
        ...style,
      }}
      {...rest}
    />
  );
}

export const selectStyle: React.CSSProperties = {
  minHeight: `${INPUT_MIN.toString()}px`,
  padding: '0 12px',
  borderRadius: '6px',
  fontSize: '16px',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  border: '1px solid var(--color-border-primary)',
};

export function Panel(props: {
  readonly children: React.ReactNode;
  readonly labelledBy?: string;
  readonly style?: React.CSSProperties;
}): React.JSX.Element {
  return (
    <section
      aria-labelledby={props.labelledBy}
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        borderRadius: 'var(--radius-card)',
        padding: '20px',
        ...props.style,
      }}
    >
      {props.children}
    </section>
  );
}

export function Notice(props: {
  readonly tone: keyof typeof TONE;
  readonly children: React.ReactNode;
  readonly role?: 'alert' | 'status';
}): React.JSX.Element {
  return (
    <div
      role={props.role}
      style={{
        backgroundColor: TONE[props.tone].bg,
        borderLeft: `4px solid ${TONE[props.tone].border}`,
        borderRadius: '6px',
        padding: '12px 16px',
        fontSize: '16px',
        color: 'var(--color-text-primary)',
      }}
    >
      {props.children}
    </div>
  );
}

export const muted: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--color-text-secondary)',
};

/** "1 row", "3 rows". Every count on the import screens goes through this. */
export function plural(n: number, one: string, many: string = `${one}s`): string {
  return `${n.toString()} ${n === 1 ? one : many}`;
}
